import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { DOMAIN_LABELS, domainColor } from '@/api/sat';
import { SHORT_DOMAIN_LABELS } from '@/components/sat/journey/studentCopy';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { cn } from '@/lib/utils';
import { MantaCharacter } from '../MantaCharacter';
import { MantaDriver } from '../mantaDriver';
import { MANTA_TEMPO, type MantaState } from '../mantaMotion';
import { useMediaMax, useStageScale } from '../stage';
import {
  CAPTIONS,
  CLIMB_CUE,
  DIAGNOSTIC,
  DOMAINS,
  DESKTOP,
  MOBILE,
  RANK_ORDER,
  TIMING,
  aboveAnchor,
  focusAnchor,
  freshRings,
  hopPathD,
  isProven,
  placeRing,
  repShown,
  queueOf,
  restAnchor,
  scanEnds,
  scanPathD,
  scoreFor,
  sectionOf,
  type Beat,
  type Placement,
  type Pt,
  type RingModel,
  type Section,
  type StageLayout,
  type StageState,
} from './methodModel';

/**
 * v3 method stage (specs/012-manta-character/DESIGN.md §11): the manta, Mentara, acts on
 * the student's eight areas, and the areas move. See `methodModel.ts` for the story,
 * the score rules and the geometry; this file is the stage, the script and the input.
 *
 * Layers: one SVG (the line, the rings, the score) and the character in an HTML carrier
 * above it (§9: nothing clips it, and the SVG never repaints while it swims). Rings move
 * with CSS transitions on two nested groups (x on the outer, y and scale on the inner), so
 * giving the two axes different easings bends every move into an arc for free.
 */

const GREEN = 'rgb(16 185 129)';
/** Green text: emerald 600 on light, 400 on dark, so small type keeps its contrast. */
const GREEN_TEXT_CLASS = 'fill-emerald-600 dark:fill-emerald-400';

/** How a ring travels to its next place. Each kind has its own duration and easing per axis. */
type MoveKind = 'none' | 'sort' | 'rise' | 'tow' | 'shift' | 'drop';

const MOVES: Record<Exclude<MoveKind, 'none'>, { ms: number; ex: string; ey: string; delay?: number }> = {
  // the sort: every ring slides to its place in line; the y lane (up when moving right, down when
  // moving left) is applied for the first half so they pass each other instead of overlapping
  sort: { ms: TIMING.sortMs, ex: 'cubic-bezier(.65,0,.35,1)', ey: 'cubic-bezier(.45,0,.55,1)' },
  // comes forward on its own: up first, then across
  rise: { ms: TIMING.riseMs, ex: 'cubic-bezier(.55,0,.35,1)', ey: 'cubic-bezier(.2,.75,.3,1)' },
  // towed behind the manta: same as rise, a beat later, so it reads as following
  tow: { ms: TIMING.riseMs, ex: 'cubic-bezier(.55,0,.35,1)', ey: 'cubic-bezier(.25,.7,.3,1)', delay: 110 },
  // the line closes up
  shift: { ms: 420, ex: 'cubic-bezier(.45,0,.25,1)', ey: 'cubic-bezier(.45,0,.25,1)' },
  // settles into its slot in the score: across first, then down (a gentle throw)
  drop: { ms: TIMING.dropMs, ex: 'cubic-bezier(.25,.6,.35,1)', ey: 'cubic-bezier(.6,0,.85,.55)' },
};

const LANE = 16;

export type MethodInitial = 'start' | 'overview' | 'done';

export interface MantaMethodProps {
  /** Tempo multiplier (the dev page's slider). */
  speed?: number;
  /** Force reduced motion. Defaults to the OS preference. */
  reduced?: boolean;
  /** Play once when the stage is at least half in view. */
  autoplay?: boolean;
  /**
   * The frame before anything plays. `start` is the prerender frame. `overview` (reduced
   * motion) shows every kind of unit at once: two proven in the score, one at the focus spot,
   * the rest in line. `done` (returning visitor) is the finished course.
   */
  initial?: MethodInitial;
  /** Show Play and Reset as well as Replay (dev page). */
  devControls?: boolean;
  /** Classes for the text rows only (caption, footnote), so the stage itself can bleed to the screen edge. */
  textClassName?: string;
  id?: string;
  className?: string;
}

export function MantaMethod({
  speed = 1,
  reduced: reducedProp,
  autoplay = false,
  initial = 'start',
  devControls = false,
  textClassName,
  id,
  className,
}: MantaMethodProps) {
  const prefersReduced = usePrefersReducedMotion();
  const reduced = reducedProp ?? prefersReduced;
  const mobile = useMediaMax(639);
  const L = mobile ? MOBILE : DESKTOP;

  const wrapRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<SVGSVGElement>(null);
  const hopRef = useRef<SVGPathElement>(null);
  const carrierRef = useRef<HTMLDivElement>(null);
  const driverRef = useRef<MantaDriver | null>(null);
  const ringEls = useRef<(SVGGElement | null)[]>([]);
  const { scale, scaleRef } = useStageScale(stageRef, L.vb.w);

  const [rings, setRings] = useState<RingModel[]>(freshRings);
  const [sorted, setSorted] = useState(false);
  const [focus, setFocus] = useState<number | null>(null);
  /** Rings that have landed in the score. The score only counts these. */
  const [banked, setBanked] = useState<number[]>([]);
  const [scoreOn, setScoreOn] = useState(false);
  const [lanes, setLanes] = useState<number[] | null>(null);
  const [beat, setBeat] = useState<Beat>('start');
  const [rig, setRig] = useState<MantaState>('idle');
  const [facing, setFacing] = useState<-1 | 1>(1);
  const [look, setLook] = useState<{ x: number; y: number } | null>(null);
  const [glance, setGlance] = useState<{ x: number; y: number } | null>(null);
  const [boost, setBoost] = useState(1);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState('Example course. Tap any area to send the manta there.');
  const [hover, setHover] = useState<number | null>(null);
  const [tab, setTab] = useState(0);
  const [inView, setInView] = useState(true);
  /** The driver is up and owns the carrier (false during prerender and first paint). */
  const [ready, setReady] = useState(false);

  // refs the async script reads (state is stale inside a running sequence)
  const moves = useRef<MoveKind[]>(DOMAINS.map(() => 'none'));
  const timers = useRef<number[]>([]);
  const token = useRef(0);
  const speedRef = useRef(speed);
  const boostRef = useRef(1);
  const reducedRef = useRef(reduced);
  const LRef = useRef(L);
  const stateRef = useRef<StageState>({ rings, sorted, focus });
  speedRef.current = speed;
  reducedRef.current = reduced;
  LRef.current = L;
  stateRef.current = { rings, sorted, focus };

  const tempo = () => speedRef.current * boostRef.current;
  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };
  const after = (ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  };
  const wait = (ms: number) => new Promise<void>((r) => after((reducedRef.current ? Math.min(ms, 280) : ms) / tempo(), r));
  const setBoostBoth = (b: number) => {
    boostRef.current = b;
    setBoost(b);
  };
  const setMove = (i: number, kind: MoveKind) => (moves.current[i] = reducedRef.current ? 'none' : kind);
  const patch = (i: number, fn: (r: RingModel) => Partial<RingModel>) =>
    setRings((rs) => {
      const next = rs.map((r, k) => (k === i ? { ...r, ...fn(r) } : r));
      stateRef.current = { ...stateRef.current, rings: next };
      return next;
    });
  const focusOn = (i: number | null) => {
    stateRef.current = { ...stateRef.current, focus: i };
    setFocus(i);
  };
  const bank = (i: number) => setBanked((b) => (b.includes(i) ? b : [...b, i]));
  const label = (i: number) => SHORT_DOMAIN_LABELS[DOMAINS[i]];

  // ── the character ──────────────────────────────────────────────────────────

  useEffect(() => {
    const p = hopRef.current;
    const c = carrierRef.current;
    if (!p || !c || typeof p.getTotalLength !== 'function') return; // jsdom: no path geometry
    const d = new MantaDriver(p, c, { mantaW: L.mantaW, hover: () => ({ dx: 0, dy: 0 }), maxBank: 9 }, {
      scale: () => scaleRef.current,
      speed: () => tempo(),
      reduced: () => reducedRef.current,
      onRig: setRig,
      onFacing: setFacing,
      onLook: setLook,
    });
    driverRef.current = d;
    setReady(true);
    const s = stateRef.current;
    placeAt(s.focus != null ? focusAnchor(L) : restAnchor(L, s.rings.every(isProven)));
    return () => {
      d.destroy();
      driverRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [L]);

  // the render that hands the carrier to the driver clears the % placement; put it back in px
  useLayoutEffect(() => {
    const d = driverRef.current;
    if (ready && d) placeAt(d.anchor(), facing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // the stage resized: re-render the carrier at the new scale (no motion)
  useEffect(() => {
    const d = driverRef.current;
    if (d && !running) placeAt(d.anchor());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  function placeAt(pt: Pt, face: -1 | 1 = 1) {
    const d = driverRef.current;
    const p = hopRef.current;
    if (!d || !p) return;
    p.setAttribute('d', hopPathD(pt, pt));
    d.resetPath(0);
    d.place(0, false);
    d.face(face, false);
  }

  /** Swim to a point along a fresh arc. Interrupting a hop keeps its momentum. */
  function hopTo(pt: Pt, opts: { ms?: number; arrive?: boolean; lift?: number; onProgress?: (u: number) => void } = {}) {
    const d = driverRef.current;
    const p = hopRef.current;
    if (!d || !p) return Promise.resolve();
    p.setAttribute('d', hopPathD(d.anchor(), pt, d.heading(), opts.lift));
    d.resetPath(0);
    return d.swimTo(d.totalLength, { hover: false, ms: opts.ms, arrive: opts.arrive, onProgress: (_l, u) => opts.onProgress?.(u) });
  }

  /** The diagnostic pass: one path from the work spot, down to the line and along it. */
  function scan(opts: { onProgress: () => void }) {
    const d = driverRef.current;
    const p = hopRef.current;
    if (!d || !p) return Promise.resolve();
    p.setAttribute('d', scanPathD(LRef.current, d.anchor()));
    d.resetPath(0);
    return d.swimTo(d.totalLength, { hover: false, ms: TIMING.scanMs, arrive: false, onProgress: opts.onProgress });
  }

  const gesture = (state: MantaState, ms: number) => {
    if (reducedRef.current) return;
    setRig(state);
    after(ms / tempo(), () => setRig((r) => (r === state ? 'idle' : r)));
  };

  // ── beats ──────────────────────────────────────────────────────────────────

  const reveal = (i: number) => {
    if (stateRef.current.rings[i].phase !== 'unknown') return;
    patch(i, () => ({ phase: 'revealed', shown: DIAGNOSTIC[DOMAINS[i]] }));
  };

  /** The diagnostic hands out the order: every ring slides to its place in line. */
  function sortLine() {
    const s = stateRef.current;
    const L = LRef.current;
    const lane = DOMAINS.map((_, i) => {
      const from = placeRing(i, s, L).x;
      const to = placeRing(i, { ...s, sorted: true }, L).x;
      return to > from + 1 ? -LANE : to < from - 1 ? LANE : 0;
    });
    DOMAINS.forEach((_, i) => setMove(i, 'sort'));
    stateRef.current = { ...stateRef.current, sorted: true };
    setSorted(true);
    if (!reducedRef.current) {
      setLanes(lane);
      after(TIMING.sortMs / 2 / tempo(), () => setLanes(null));
    }
  }

  /** The manta picks up a ring from the line and brings it to the focus spot. */
  async function tow(i: number, alive: () => boolean) {
    const L = LRef.current;
    const at = placeRing(i, stateRef.current, L);
    if (at.place !== 'focus') {
      setGlance({ x: 0.3, y: 0.9 });
      await hopTo(aboveAnchor(L, at, at.r), { ms: TIMING.towHopMs });
      if (!alive()) return;
      bringForward(i, 'tow');
      await hopTo(focusAnchor(L), { ms: TIMING.towHopMs + 40 });
      if (!alive()) return;
    } else {
      await hopTo(focusAnchor(L));
      if (!alive()) return;
    }
    driverRef.current?.face(1, false);
    setGlance({ x: 0.9, y: 0.15 });
  }

  /** A ring comes forward to the focus spot; the line closes up behind it. */
  function bringForward(i: number, kind: MoveKind) {
    const prev = stateRef.current.focus;
    DOMAINS.forEach((_, k) => setMove(k, k === i ? kind : 'shift'));
    if (prev != null && prev !== i) setMove(prev, isProven(stateRef.current.rings[prev]) ? 'drop' : 'shift');
    focusOn(i);
  }

  async function practice(i: number, reps: number, alive: () => boolean) {
    const start = stateRef.current.rings[i].shown ?? DIAGNOSTIC[DOMAINS[i]];
    patch(i, () => ({ phase: 'practising', reps: 0, repsTotal: reps }));
    for (let k = 1; k <= reps; k++) {
      if (!alive()) return;
      patch(i, () => ({ shown: repShown(start, k, reps), reps: k }));
      setStatus(`${label(i)}: practice set ${k} of ${reps} done.`);
      gesture('nod', MANTA_TEMPO.nodMs);
      await wait(TIMING.repMs);
    }
  }

  function prove(i: number, alive: () => boolean): Promise<void> {
    if (reducedRef.current) {
      patch(i, () => ({ phase: 'proven', shown: 100 }));
      setStatus(`${label(i)} proven.`);
      return Promise.resolve();
    }
    setRig('master');
    const sp = tempo();
    after(MANTA_TEMPO.checkAtMs / sp, () => patch(i, () => ({ shown: 100 })));
    after(MANTA_TEMPO.flagAtMs / sp, () => patch(i, () => ({ phase: 'proven' })));
    return new Promise<void>((resolve) =>
      after(MANTA_TEMPO.masterMs / sp, () => {
        if (!alive()) return;
        setRig('idle');
        setStatus(`${label(i)} proven.`);
        resolve();
      }),
    );
  }

  /** The proven ring settles into its slot in the score; the score ticks when it lands. */
  function drop(i: number): Promise<void> {
    setMove(i, 'drop');
    focusOn(null);
    setGlance({ x: 0.35, y: 1 });
    return new Promise<void>((resolve) =>
      after((reducedRef.current ? 0 : TIMING.dropMs) / tempo(), () => {
        bank(i);
        resolve();
      }),
    );
  }

  /** Spaced review: a proven ring is pulled back up to the manta, checked, and goes home. */
  async function review(i: number, alive: () => boolean) {
    const L = LRef.current;
    const d = driverRef.current;
    if (d) {
      const a = d.anchor();
      const f = focusAnchor(L);
      if (Math.hypot(a.x - f.x, a.y - f.y) > 2) {
        await hopTo(f);
        if (!alive()) return;
        d.face(1, false);
      }
    }
    setGlance({ x: 0.6, y: 0.8 });
    bringForward(i, 'rise');
    await wait(TIMING.riseMs);
    if (!alive()) return;
    patch(i, (r) => ({ phase: 'reviewed', pulse: r.pulse + 1 }));
    setGlance({ x: 0.9, y: 0.15 });
    gesture('nod', MANTA_TEMPO.nodMs);
    setStatus(`Quick review: ${label(i)}. Still green.`);
    await wait(TIMING.reviewHoldMs);
    if (!alive()) return;
    setMove(i, 'drop');
    focusOn(null);
    setGlance({ x: 0.35, y: 1 });
    await wait(TIMING.dropMs * 0.5);
  }

  // ── whole sequences ────────────────────────────────────────────────────────

  const resetTo = useCallback((mode: MethodInitial) => {
    token.current += 1;
    clearTimers();
    setBoostBoth(1);
    setRunning(false);
    setLanes(null);
    setRig('idle');
    setGlance(null);
    moves.current = DOMAINS.map(() => 'none');
    const L = LRef.current;
    let next: RingModel[] = freshRings();
    let nextFocus: number | null = null;
    let nextBanked: number[] = [];
    if (mode === 'done') {
      next = next.map(() => ({ phase: 'proven', shown: 100, reps: 0, repsTotal: 0, pulse: 0 }));
      nextBanked = [...RANK_ORDER];
    } else if (mode === 'overview') {
      const [a, b, c] = RANK_ORDER;
      next = next.map((_, i) => ({ phase: 'revealed', shown: DIAGNOSTIC[DOMAINS[i]], reps: 0, repsTotal: 0, pulse: 0 }));
      next[a] = { ...next[a], phase: 'proven', shown: 100 };
      next[b] = { ...next[b], phase: 'proven', shown: 100 };
      next[c] = { ...next[c], phase: 'practising', shown: repShown(DIAGNOSTIC[DOMAINS[c]], 1, 2), reps: 1, repsTotal: 2 };
      nextFocus = c;
      nextBanked = [a, b];
    }
    stateRef.current = { rings: next, sorted: mode !== 'start', focus: nextFocus };
    setRings(next);
    setSorted(mode !== 'start');
    setFocus(nextFocus);
    setBanked(nextBanked);
    setScoreOn(mode !== 'start');
    setBeat(mode === 'done' ? 'done' : mode === 'overview' ? 'practice' : 'start');
    setStatus(
      mode === 'done'
        ? 'Example course: all 8 areas proven.'
        : mode === 'overview'
          ? `Example course: 2 of 8 areas proven, practising ${label(nextFocus ?? 0)}.`
          : 'Example course. Tap any area to send the manta there.',
    );
    if (driverRef.current) placeAt(nextFocus != null ? focusAnchor(L) : restAnchor(L, mode === 'done'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const play = useCallback(async () => {
    resetTo('start');
    const t = ++token.current;
    const alive = () => token.current === t;
    const L = LRef.current;
    setRunning(true);
    await wait(TIMING.startHoldMs);
    if (!alive()) return;

    // 1. diagnose: one pass over the line; each result appears as the manta passes it
    setBeat('diagnose');
    setStatus('Diagnostic running.');
    const lineY = scanEnds(L).from.y;
    setGlance({ x: 0.4, y: 0.8 });
    await scan({
      onProgress: () => {
        const d = driverRef.current;
        const a = d?.anchor();
        const h = d?.heading();
        // only on the pass along the line (low enough, heading right), not on the swoop down
        if (!a || !h || h.dx <= 0.3 || a.y < lineY - 12) return;
        DOMAINS.forEach((_, i) => a.x + 0.22 * L.mantaW >= L.rowX[i] && reveal(i));
      },
    });
    if (!alive()) return;
    DOMAINS.forEach((_, i) => reveal(i));
    setScoreOn(true);

    // 2. sort: the rings line up weakest first while the manta turns to watch
    setBeat('sort');
    setStatus('Diagnostic done. Your areas, weakest first.');
    driverRef.current?.face(-1, false);
    setGlance({ x: 0.5, y: 0.6 });
    sortLine();
    await wait(TIMING.sortMs + 120);
    if (!alive()) return;

    // 3 to 5: the weakest area, from the line to green to the score
    const [first, second, ...others] = RANK_ORDER;
    setBeat('first');
    setStatus(`Weakest first: ${label(first)}.`);
    await tow(first, alive);
    if (!alive()) return;
    setBeat('practice');
    await practice(first, TIMING.repsFirst, alive);
    if (!alive()) return;
    setBeat('prove');
    await prove(first, alive);
    if (!alive()) return;
    await wait(TIMING.flagHoldMs);
    if (!alive()) return;
    // the manta sets off for the next area while the proven one is still settling into the score
    void drop(first);
    await wait(TIMING.dropMs * 0.5);
    if (!alive()) return;

    // 6. the next weakest
    setBeat('next');
    setStatus(`Next weakest: ${label(second)}.`);
    await tow(second, alive);
    if (!alive()) return;
    setBeat('practice');
    await practice(second, TIMING.repsSecond, alive);
    if (!alive()) return;
    setBeat('prove');
    await prove(second, alive);
    if (!alive()) return;
    await wait(TIMING.flagHoldMs);
    if (!alive()) return;
    await drop(second);
    if (!alive()) return;

    // 7. review: the first one is pulled back up once, and stays green
    setBeat('review');
    await review(first, alive);
    if (!alive()) return;

    // 8. the rest, quicker: each comes forward on its own, one set, the check, into the score
    setBeat('rest');
    setBoostBoth(TIMING.restBoost);
    let landing: Promise<void> = Promise.resolve();
    for (const i of others) {
      if (!alive()) return;
      setGlance({ x: 0.7, y: 0.9 });
      bringForward(i, 'rise');
      await wait(TIMING.riseMs * 0.85);
      if (!alive()) return;
      setGlance({ x: 0.9, y: 0.15 });
      await practice(i, 1, alive);
      if (!alive()) return;
      await prove(i, alive);
      if (!alive()) return;
      landing = drop(i);
      await wait(TIMING.dropMs * 0.35);
    }
    await landing;
    if (!alive()) return;
    setBoostBoth(1);

    // 9. done
    setBeat('done');
    setStatus('Example course: all 8 areas proven.');
    setGlance({ x: 0.2, y: 0.9 });
    await hopTo(restAnchor(L, true));
    if (!alive()) return;
    driverRef.current?.face(1, false);
    setGlance(null);
    if (!reducedRef.current) {
      setRig('finale');
      await wait(MANTA_TEMPO.finaleMs);
      if (!alive()) return;
      setRig('idle');
    }
    setRunning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTo]);

  /** Tap an area: the manta goes there and does what that area needs next. */
  const send = useCallback(async (i: number) => {
    const t = ++token.current;
    const alive = () => token.current === t;
    clearTimers();
    setBoostBoth(1);
    setLanes(null);
    setRunning(true);
    const L = LRef.current;
    const r = stateRef.current.rings[i];
    if (r.phase === 'unknown') {
      setBeat('diagnose');
      setStatus(`Measuring ${label(i)}.`);
      const at = placeRing(i, stateRef.current, L);
      setGlance({ x: 0.3, y: 0.9 });
      await hopTo(aboveAnchor(L, at, at.r));
      if (!alive()) return;
      reveal(i);
      gesture('nod', MANTA_TEMPO.nodMs);
      const all = stateRef.current.rings.every((x) => x.phase !== 'unknown');
      if (all) setScoreOn(true);
      setStatus(`${label(i)} measured. The diagnostic measures every area before practice starts.`);
    } else if (isProven(r)) {
      setBeat('review');
      await review(i, alive);
    } else {
      setBeat('first');
      setStatus(`${label(i)} next.`);
      await tow(i, alive);
      if (!alive()) return;
      setBeat('practice');
      await practice(i, 2, alive);
      if (!alive()) return;
      setBeat('prove');
      await prove(i, alive);
      if (!alive()) return;
      await drop(i);
      if (!alive()) return;
      if (stateRef.current.rings.every(isProven)) setBeat('done');
    }
    if (!alive()) return;
    setRunning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // initial frame (it can change after hydration: returning visitor, reduced motion)
  useLayoutEffect(() => {
    if (initial !== 'start') resetTo(initial);
  }, [initial, resetTo]);

  // in view: autoplay once, and pause the character's clock while off screen
  const played = useRef(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[entries.length - 1];
        setInView(e.isIntersecting);
        if (autoplay && !played.current && initial === 'start' && !reducedRef.current && e.intersectionRatio >= 0.5) {
          played.current = true;
          after(TIMING.autoplayDelayMs, () => void play());
        }
      },
      { threshold: [0, 0.5] },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, initial, play]);

  useEffect(() => () => clearTimers(), []);

  // ── input ──────────────────────────────────────────────────────────────────

  const onKey = (i: number) => (e: KeyboardEvent<SVGGElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void send(i);
      return;
    }
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const n = (i + step + 8) % 8;
    setTab(n);
    ringEls.current[n]?.focus();
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (box) driverRef.current?.setPointer({ x: e.clientX - box.left, y: e.clientY - box.top });
  };

  // ── render ─────────────────────────────────────────────────────────────────

  const state: StageState = { rings, sorted, focus };
  const score = useMemo(() => scoreFor(banked), [banked]);
  const sp = speed * boost;
  const proven = banked.length;
  const tip = hover != null ? { i: hover, at: placeRing(hover, state, L) } : null;
  const showReplay = !running && beat !== 'start';
  // Until the driver exists (prerender, first paint) the carrier is placed in % of the stage at
  // the resting spot, so the character is where it belongs before anything is measured. Once the
  // driver is up it owns the carrier's transform in px.
  const rest = restAnchor(L);
  const carrierStyle: CSSProperties = ready
    ? { width: L.mantaW * scale, transformOrigin: '0 0' }
    : {
        width: `${(L.mantaW / L.vb.w) * 100}%`,
        left: `${(rest.x / L.vb.w) * 100}%`,
        top: `${(rest.y / L.vb.h) * 100}%`,
        transform: 'translate(-69.9%, -51.24%)',
      };

  return (
    <section
      id={id}
      className={cn('text-foreground', className)}
      aria-label="How the course works, with an example student"
      data-testid="manta-method"
      data-beat={beat}
      data-status={running ? 'playing' : beat === 'start' ? 'idle' : 'done'}
      data-proven={proven}
      data-score={scoreOn ? score.total : undefined}
      data-reduced={reduced ? 'true' : undefined}
    >
      <p
        key={beat}
        className={cn('v3-caption min-h-[2.75rem] text-pretty text-lg font-semibold leading-snug tracking-[-0.01em] sm:min-h-0 sm:text-xl', textClassName)}
        data-testid="v3-caption"
      >
        {CAPTIONS[beat]}
      </p>

      <div
        ref={wrapRef}
        className="relative mt-3 w-full"
        onPointerMove={onPointerMove}
        onPointerLeave={() => driverRef.current?.setPointer(null)}
      >
        <svg
          ref={stageRef}
          viewBox={`0 0 ${L.vb.w} ${L.vb.h}`}
          className="block h-auto w-full overflow-visible"
          role="group"
          aria-label="Your eight areas and the example score"
        >
          <path ref={hopRef} d="M 0 0 L 1 0" fill="none" stroke="none" />
          <Line L={L} sorted={sorted} empty={sorted && queueOf(state).length === 0} />
          <Score L={L} score={score} on={scoreOn} reduced={reduced} speed={speed} />
          {DOMAINS.map((domain, i) => {
            const at = placeRing(i, state, L);
            return (
              <Ring
                key={domain}
                i={i}
                ring={rings[i]}
                at={at}
                L={L}
                kind={moves.current[i]}
                lane={lanes?.[i] ?? 0}
                speed={sp}
                reduced={reduced}
                tabIndex={tab === i ? 0 : -1}
                elRef={(el) => (ringEls.current[i] = el)}
                onSend={() => {
                  setHover(null);
                  void send(i);
                }}
                onKey={onKey(i)}
                onHover={(on) => setHover((h) => (on ? i : h === i ? null : h))}
                onFocusRing={() => setTab(i)}
              />
            );
          })}
          <FocusLabel L={L} i={focus} ring={focus != null ? rings[focus] : null} delayMs={reduced ? 0 : Math.round((TIMING.riseMs * 0.7) / sp)} />
        </svg>

        <div
          ref={carrierRef}
          className="pointer-events-none absolute left-0 top-0 text-primary will-change-transform dark:text-[#fda4af]"
          style={carrierStyle}
          data-testid="manta-carrier"
        >
          <MantaCharacter
            state={rig}
            facing={facing}
            lookAt={look ?? glance}
            size={ready ? L.mantaW * scale : undefined}
            speed={sp}
            reducedMotion={reduced}
            paused={!inView}
          />
        </div>

        {tip && (
          <div
            role="tooltip"
            data-testid="v3-tip"
            className="pointer-events-none absolute z-10 w-52 rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-sm"
            // below the focus spot (the space between it and the line is free); above anything on
            // the line or in the score, so it never covers the score's labels
            style={
              tip.at.place === 'focus'
                ? { left: Math.max(4, Math.min(L.vb.w * scale - 212, tip.at.x * scale - 104)), top: (tip.at.y + tip.at.r + 8) * scale }
                : {
                    left: Math.max(4, Math.min(L.vb.w * scale - 212, tip.at.x * scale - 104)),
                    top: (tip.at.y - tip.at.r - 8) * scale,
                    transform: 'translateY(-100%)',
                  }
            }
          >
            <p className="font-semibold">{DOMAIN_LABELS[DOMAINS[tip.i]]}</p>
            <p className="mt-0.5 text-muted-foreground">{ringSentence(rings[tip.i], tip.i, state)}</p>
          </div>
        )}
      </div>

      <div className={cn('mt-2 flex min-h-8 items-center gap-3', textClassName)}>
        <p className="flex-1 text-xs text-muted-foreground">Example student. Yours is built from your own answers.</p>
        {devControls && (
          <>
            <button type="button" onClick={() => void play()} className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground" data-testid="v3-play">
              Play
            </button>
            <button type="button" onClick={() => resetTo('start')} className="rounded-md border px-3 py-1 text-xs hover:bg-muted" data-testid="v3-reset">
              Reset
            </button>
          </>
        )}
        {!devControls && showReplay && (
          <button
            type="button"
            onClick={() => void play()}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="v3-replay"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M2.5 8a5.5 5.5 0 1 0 1.7-4" />
              <path d="M2.5 2.5v3.2h3.2" />
            </svg>
            Replay
          </button>
        )}
      </div>
      <p className="sr-only" aria-live="polite" data-testid="v3-status">
        {status}
      </p>
      <style>{STAGE_CSS}</style>
    </section>
  );
}

function ringSentence(r: RingModel, i: number, s: StageState): string {
  switch (r.phase) {
    case 'unknown':
      return 'Not measured yet. The diagnostic measures it.';
    case 'revealed': {
      if (!s.sorted) return 'Measured. Waiting for its place in line.';
      const k = RANK_ORDER.filter((j) => !isProven(s.rings[j])).indexOf(i);
      return k === 0 ? 'Weakest area, so it comes first.' : `Number ${k + 1} in line.`;
    }
    case 'practising':
      return `Practising: set ${r.reps} of ${r.repsTotal} done. A mastery check comes next.`;
    case 'proven':
      return 'Proven in a mastery check. It will come back for a quick review.';
    case 'reviewed':
      return 'Proven, and reviewed since. Still green.';
  }
}

// ── stage pieces ─────────────────────────────────────────────────────────────

const smallCaps = 'fill-muted-foreground text-[11px] font-semibold uppercase tracking-[0.08em]';

/** The line the areas sit on, and the section names under it until the sort breaks the groups up. */
function Line({ L, sorted, empty }: { L: StageLayout; sorted: boolean; empty: boolean }) {
  const x0 = L.rowX[0] - L.rowR - 14;
  const x1 = L.rowX[7] + L.rowR + 14;
  const textX = Math.max(16, x0);
  return (
    <g aria-hidden className="v3-fade" style={{ opacity: empty ? 0 : 1 }}>
      <path d={`M ${x0} ${L.rowY} H ${x1}`} stroke="hsl(var(--border))" strokeWidth={1.5} strokeLinecap="round" />
      <g className="v3-fade" style={{ opacity: sorted ? 0 : 1 }}>
        {(['RW', 'MATH'] as const).map((s) => (
          <text key={s} x={L.sectionLabels[s].x} y={L.sectionLabels[s].y} textAnchor="middle" className={smallCaps} stroke="none">
            {s === 'RW' ? 'Reading and Writing' : 'Math'}
          </text>
        ))}
      </g>
      <text x={textX} y={L.sectionLabels.RW.y} className={cn(smallCaps, 'v3-fade')} style={{ opacity: sorted ? 1 : 0 }} stroke="none">
        Your areas, weakest first
      </text>
    </g>
  );
}

/** Before the diagnostic there is no score to show; the number's place says so in words. */
const PLACEHOLDER_TOTAL = 'after the diagnostic';
const PLACEHOLDER_SECTION = '';

/** The example score: the total out of 1600, then each section out of 800 with its four slots. */
function Score({ L, score, on, reduced, speed }: { L: StageLayout; score: ReturnType<typeof scoreFor>; on: boolean; reduced: boolean; speed: number }) {
  const S = L.score;
  const tick = TIMING.tickMs / Math.max(0.1, speed);
  return (
    <g data-testid="v3-score">
      <text x={S.label.x} y={S.label.y} className={smallCaps} stroke="none">
        Example SAT score
      </text>
      <text x={S.total.x} y={S.total.y} stroke="none" className="tabular-nums" data-testid="v3-total">
        {on ? (
          <>
            <tspan fontSize={S.total.size} fontWeight={650} letterSpacing="-0.03em" className="fill-foreground">
              <Ticker value={score.total} placeholder={PLACEHOLDER_TOTAL} ms={tick} reduced={reduced} />
            </tspan>
            <tspan dx={6} fontSize={Math.round(S.total.size * 0.32)} fontWeight={500} className="fill-muted-foreground">
              / 1600
            </tspan>
          </>
        ) : (
          <tspan fontSize={L.labelFont + 3} dy={-S.total.size * 0.28} className="fill-muted-foreground/70">
            {PLACEHOLDER_TOTAL}
          </tspan>
        )}
      </text>
      {score.rwLocked && score.mathLocked && on && (
        // once the count has settled and both sections have locked: the course keeps going (no number)
        <g className={reduced ? undefined : 'v3-climb'} data-testid="v3-climb">
          <path
            d={`M ${S.climb.x} ${S.climb.y - 3} l 4 -4.5 l 4 4.5`}
            fill="none"
            stroke={GREEN}
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <text x={S.climb.x + 13} y={S.climb.y} fontSize={L.labelFont} className="fill-muted-foreground" stroke="none">
            {CLIMB_CUE}
          </text>
        </g>
      )}
      {(['RW', 'MATH'] as const).map((s) => (
        <SectionRow key={s} L={L} section={s} value={on ? (s === 'RW' ? score.rw : score.math) : null} locked={s === 'RW' ? score.rwLocked : score.mathLocked} tick={tick} reduced={reduced} />
      ))}
      {/* the slots: home for each proven area, in exam order under its section */}
      {DOMAINS.map((d, i) => {
        const p = L.slot(i);
        return <circle key={d} cx={p.x} cy={p.y} r={L.slotR} fill="none" stroke="hsl(var(--muted-foreground) / .4)" strokeWidth={1.25} strokeDasharray="2.2 3" />;
      })}
    </g>
  );
}

function SectionRow({ L, section, value, locked, tick, reduced }: { L: StageLayout; section: Section; value: number | null; locked: boolean; tick: number; reduced: boolean }) {
  const row = L.score.rows[section];
  const line = L.score.lockLine[section];
  return (
    <g data-testid={`v3-section-${section}`} data-locked={locked}>
      <text x={row.label.x} y={row.label.y} fontSize={L.labelFont + 1} className="fill-muted-foreground" stroke="none">
        {section === 'RW' ? 'Reading and Writing' : 'Math'}
      </text>
      <text
        key={locked ? 'locked' : 'open'}
        x={row.value.x}
        y={row.value.y}
        stroke="none"
        className={cn('tabular-nums', locked && !reduced && 'v3-lock')}
        style={{ transformBox: 'fill-box', transformOrigin: 'left center' } as CSSProperties}
      >
        <tspan fontSize={row.value.size} fontWeight={650} letterSpacing="-0.02em" className={locked ? GREEN_TEXT_CLASS : 'fill-foreground'}>
          <Ticker value={value} placeholder={PLACEHOLDER_SECTION} ms={tick} reduced={reduced} />
        </tspan>
        <tspan dx={value == null ? 0 : 4} fontSize={L.labelFont} className="fill-muted-foreground">
          / 800
        </tspan>
      </text>
      <path
        d={`M ${line.x1 - L.slotR} ${line.y} H ${line.x2 + L.slotR}`}
        stroke={GREEN}
        strokeWidth={2}
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray="1 1"
        style={{ strokeDashoffset: locked ? 0 : 1, transition: reduced ? undefined : 'stroke-dashoffset 520ms cubic-bezier(.3,.7,.2,1) 120ms' }}
      />
    </g>
  );
}

/**
 * A number that counts to its new value (rounded to tens on the way, like a scaled score).
 * It writes the text node directly, so a tick never re-renders the stage.
 */
function Ticker({ value, placeholder, ms, reduced }: { value: number | null; placeholder: string; ms: number; reduced: boolean }) {
  const ref = useRef<SVGTSpanElement>(null);
  const shown = useRef<number | null>(value);
  const [first] = useState(() => (value == null ? placeholder : String(value)));
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const from = shown.current;
    if (value == null || from == null || reduced || ms <= 0 || from === value) {
      shown.current = value;
      el.textContent = value == null ? placeholder : String(value);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / ms);
      const e = 1 - Math.pow(1 - t, 3);
      const v = t >= 1 ? value : Math.round((from + (value - from) * e) / 10) * 10;
      el.textContent = String(v);
      shown.current = v;
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      shown.current = value;
    };
  }, [value, ms, reduced, placeholder]);
  return <tspan ref={ref}>{first}</tspan>;
}

/** The focused area's name and what is happening to it. One focal point: only the focus is labelled. */
function FocusLabel({ L, i, ring, delayMs }: { L: StageLayout; i: number | null; ring: RingModel | null; delayMs: number }) {
  if (i == null || !ring) return null;
  const line =
    ring.phase === 'practising'
      ? `Practice set ${ring.reps} of ${ring.repsTotal}`
      : ring.phase === 'proven'
        ? 'Proven'
        : ring.phase === 'reviewed'
          ? 'Quick review'
          : 'Weakest left';
  const p = L.focusLabel;
  return (
    <g key={i} className="v3-label-in" style={{ animationDelay: `${delayMs}ms` }} aria-hidden>
      <text x={p.x} y={p.y} textAnchor={p.anchor} fontSize={L.labelFont + 4} fontWeight={600} className="fill-foreground" stroke="none">
        {SHORT_DOMAIN_LABELS[DOMAINS[i]]}
      </text>
      <text
        x={p.x}
        y={p.y + L.labelFont + 7}
        textAnchor={p.anchor}
        fontSize={L.labelFont}
        stroke="none"
        className={ring.phase === 'proven' || ring.phase === 'reviewed' ? GREEN_TEXT_CLASS : 'fill-muted-foreground'}
      >
        {line}
      </text>
    </g>
  );
}

interface RingProps {
  i: number;
  ring: RingModel;
  at: Placement;
  L: StageLayout;
  kind: MoveKind;
  lane: number;
  speed: number;
  reduced: boolean;
  tabIndex: number;
  elRef: (el: SVGGElement | null) => void;
  onSend: () => void;
  onKey: (e: KeyboardEvent<SVGGElement>) => void;
  onHover: (on: boolean) => void;
  onFocusRing: () => void;
}

/**
 * One area. Drawn once at `baseR` and scaled to its place; stroke widths divide by the
 * scale so every ring keeps the same line weight on screen. Unknown: a dashed hairline.
 * Measured: a faint track and an arc in the area's colour, as full as it is strong.
 * Proven: green; at the focus spot it carries a flag, in its slot a check.
 */
function Ring({ i, ring, at, L, kind, lane, speed, reduced, tabIndex, elRef, onSend, onKey, onHover, onFocusRing }: RingProps) {
  const R = L.baseR;
  const C = 2 * Math.PI * R;
  const s = at.r / R;
  const m = kind === 'none' || reduced ? null : MOVES[kind];
  const ms = m ? Math.round(m.ms / speed) : 0;
  const delay = m?.delay ? Math.round(m.delay / speed) : 0;
  const yMs = kind === 'sort' ? Math.round(ms / 2) : ms;
  const tone = domainColor(DOMAINS[i]);
  const green = isProven(ring);
  const known = ring.phase !== 'unknown';
  const shown = ring.shown ?? 0;
  const atFocus = at.place === 'focus';
  const sw = (atFocus ? 3 : 2.25) / s;
  const fade = reduced ? undefined : `opacity ${Math.round(220 / speed)}ms ease-out`;
  const strokeT = reduced ? undefined : `stroke-width ${ms || 200}ms ease-out ${delay}ms, stroke ${Math.round(200 / speed)}ms`;

  return (
    <g
      ref={elRef}
      role="button"
      tabIndex={tabIndex}
      aria-label={`${DOMAIN_LABELS[DOMAINS[i]]}, ${sectionOf(i) === 'RW' ? 'Reading and Writing' : 'Math'}. ${ringSentenceShort(ring)} Send the manta here.`}
      onClick={onSend}
      onKeyDown={onKey}
      onPointerEnter={() => onHover(true)}
      onPointerLeave={() => onHover(false)}
      onFocus={() => {
        onHover(true);
        onFocusRing();
      }}
      onBlur={() => onHover(false)}
      className="v3-ring cursor-pointer outline-none"
      data-testid="v3-ring"
      data-domain={DOMAINS[i]}
      data-phase={ring.phase}
      data-place={at.place}
      style={{ transform: `translate(${at.x}px, 0px)`, transition: m ? `transform ${ms}ms ${m.ex} ${delay}ms` : undefined }}
    >
      <g
        style={{
          transform: `translate(0px, ${at.y + lane}px) scale(${s})`,
          transition: m ? `transform ${yMs}ms ${m.ey} ${delay}ms` : undefined,
        }}
      >
        <g key={ring.pulse} className={ring.pulse && !reduced ? 'v3-pulse' : undefined}>
          <circle r={R + 10 / s} fill="transparent" />
          {/* focus ring outline for keyboard users */}
          <circle className="v3-focus-ring" r={R + 7 / s} fill="none" stroke="hsl(var(--ring))" strokeWidth={2 / s} />
          {/* a soft disc behind the focused area: the one focal point */}
          <circle r={R} fill={green ? 'rgb(16 185 129 / .08)' : domainColor(DOMAINS[i], atFocus ? 0.09 : 0)} style={{ transition: fade }} />
          <circle
            r={R}
            fill="none"
            stroke={green ? GREEN : known ? 'hsl(var(--border))' : 'hsl(var(--muted-foreground) / .45)'}
            strokeDasharray={known ? undefined : `${C / 40} ${C / 40}`}
            style={{ strokeWidth: sw, transition: strokeT }}
          />
          {known && !green && (
            <circle
              r={R}
              fill="none"
              stroke={tone}
              strokeLinecap="round"
              strokeDasharray={`${(C * shown) / 100} ${C}`}
              transform="rotate(-90)"
              style={{ strokeWidth: sw, transition: reduced ? undefined : `stroke-dasharray ${Math.round(420 / speed)}ms cubic-bezier(.2,.8,.2,1), ${strokeT}` }}
              data-testid="v3-arc"
            />
          )}
          {green && (
            <>
              <path
                d={`M ${-R * 0.38} ${R * 0.02} L ${-R * 0.1} ${R * 0.3} L ${R * 0.42} ${-R * 0.28}`}
                fill="none"
                stroke={GREEN}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ strokeWidth: 2.4 / s, opacity: atFocus ? 0 : 1, transition: fade }}
              />
              <g className={atFocus && !reduced ? (ring.phase === 'reviewed' ? 'v3-wiggle' : 'v3-pop') : undefined} style={{ opacity: atFocus ? 1 : 0, transition: fade }} data-testid="v3-flag">
                <circle cx={R * 0.72} cy={-R * 0.72} r={10.5} fill={GREEN} stroke="hsl(var(--background))" strokeWidth={2} />
                <path d={`M${R * 0.72 - 4} ${-R * 0.72 - 5.5} v11 M${R * 0.72 - 4} ${-R * 0.72 - 5.5} h7.5 l-2.4 2.8 2.4 2.8 h-7.5`} fill="none" stroke="#fff" strokeWidth={1.6} strokeLinejoin="round" />
              </g>
            </>
          )}
        </g>
      </g>
    </g>
  );
}

function ringSentenceShort(r: RingModel): string {
  switch (r.phase) {
    case 'unknown':
      return 'Not measured yet.';
    case 'revealed':
      return 'Measured.';
    case 'practising':
      return `Practice set ${r.reps} of ${r.repsTotal} done.`;
    case 'proven':
      return 'Proven.';
    case 'reviewed':
      return 'Proven and reviewed.';
  }
}

const STAGE_CSS = `
@keyframes v3-caption-in { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
.v3-caption { animation: v3-caption-in 280ms ease-out both; }
@keyframes v3-label-in { from { opacity: 0; } to { opacity: 1; } }
.v3-label-in { animation: v3-label-in 260ms ease-out both; }
.v3-fade { transition: opacity 320ms ease-out; }
@keyframes v3-pulse { 0% { transform: scale(1); } 40% { transform: scale(1.1); } 100% { transform: scale(1); } }
.v3-pulse { animation: v3-pulse 520ms ease-out both; transform-box: fill-box; transform-origin: center; }
@keyframes v3-pop { from { opacity: 0; transform: scale(.4); } to { opacity: 1; transform: scale(1); } }
.v3-pop { animation: v3-pop 200ms cubic-bezier(.2,.8,.2,1) both; transform-box: fill-box; transform-origin: center; }
@keyframes v3-wiggle { 0%,100% { transform: rotate(0); } 30% { transform: rotate(-14deg); } 65% { transform: rotate(10deg); } }
.v3-wiggle { animation: v3-wiggle 480ms ease-in-out both; transform-box: fill-box; transform-origin: center; }
@keyframes v3-lock { 0% { transform: scale(1); } 35% { transform: scale(1.16); } 100% { transform: scale(1); } }
.v3-lock { animation: v3-lock 560ms cubic-bezier(.2,.8,.2,1) both; }
@keyframes v3-climb { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
.v3-climb { animation: v3-climb 420ms ease-out 900ms both; }
.v3-ring .v3-focus-ring { opacity: 0; }
.v3-ring:focus-visible .v3-focus-ring { opacity: 1; }
@media (prefers-reduced-motion: reduce) {
  .v3-caption, .v3-label-in, .v3-pulse, .v3-pop, .v3-wiggle, .v3-lock, .v3-climb { animation: none; }
  .v3-fade { transition: none; }
}
`;
